import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DOC_TYPES, fmtDate, isoOffset } from "@/lib/helpers";
import { FrDateInput, FrTimeInput } from "@/components/FrDateInput";
import { EstablishmentPicker } from "@/components/EstablishmentPicker";
import { Search, X, ChevronDown, Loader2 } from "lucide-react";

// Saisie rapide régulation (2e écran, pendant la facturation) : Patient → Type → Établissement → Service → Date transport → (chauffeur/consigne) → Créer.
// Patient nouveau : nom + prénom suffisent (DOB facultative). Homonyme → choix explicite, jamais de fusion silencieuse. Entrée = créer.
export default function CreateRequestDialog({ establishments, drivers = [], reloadEstablishments, onClose, onCreated, initial = {}, submitPath = "/requests", title = "Nouvelle demande" }) {
  const [search, setSearch] = useState(initial.names ? `${initial.names.last_name} ${initial.names.first_name}` : "");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(initial.patient || null);
  const [np, setNp] = useState({ last_name: initial.names?.last_name || "", first_name: initial.names?.first_name || "", birth_date: "" });
  const [f, setF] = useState({ document_type: "pmt", establishment_id: "", service: "", contact_person: "", transport_date: isoOffset(0), transport_time: "", available_from: "", deadline: "", comment: "", assigned_driver_id: "", ...initial.fields });
  const [more, setMore] = useState(false);
  const [similar, setSimilar] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);
  const isReq = f.document_type === "pmt_request";
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => { if (!patient) setTimeout(() => searchRef.current?.focus(), 50); }, [patient]);
  useEffect(() => { if (initial.names) { const [ln] = [initial.names.last_name]; api.get("/patients", { params: { search: ln } }).then((r) => setResults(r.data.slice(0, 6))).catch(() => {}); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = async (q) => {
    setSearch(q); setSimilar(null);
    // « DUPONT Jean » → pré-remplit nom/prénom du nouveau patient
    const [ln, ...rest] = q.trim().split(/\s+/);
    setNp((s) => ({ ...s, last_name: ln || "", first_name: rest.join(" ") }));
    if (q.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get("/patients", { params: { search: ln } }); setResults(data.slice(0, 6)); } catch { setResults([]); }
  };

  const submit = async (forceNew = false) => {
    setError("");
    if (!patient && (!np.last_name.trim() || !np.first_name.trim())) { setError("Patient : saisissez au moins le nom et le prénom."); return; }
    if (!f.establishment_id) { setError("Choisissez un établissement."); return; }
    if (isReq && !f.transport_date) { setError("La date du transport est obligatoire pour demander une PMT."); return; }
    setSaving(true);
    try {
      const body = { ...f, transport_time: f.transport_time || null, available_from: f.available_from || null, deadline: f.deadline || null, assigned_driver_id: f.assigned_driver_id || null,
        patient_id: patient?.id || null, patient_last_name: patient ? null : np.last_name, patient_first_name: patient ? null : np.first_name,
        patient_birth_date: patient ? null : (np.birth_date || null), force_new_patient: forceNew };
      const { data } = await api.post(submitPath, body);
      toast.success(data.patient_created ? "Patient créé et demande enregistrée" : "Demande créée");
      onCreated(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "similar_patient") setSimilar(d.similar);
      else setError(apiError(d) || e.message);
      setSaving(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA" && !e.target.closest("[data-no-enter]")) { e.preventDefault(); submit(); } };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg sm:max-w-2xl max-h-[92vh] overflow-auto" onKeyDown={onKey} data-testid="create-request-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Patient <span className="text-slate-400 font-normal">— nom prénom (date de naissance facultative)</span></Label>
            {patient ? (
              <div data-testid="selected-patient" className="mt-1 flex items-center justify-between bg-accent-50 border border-accent-200 rounded-lg px-3 py-2">
                <span className="font-semibold text-slate-800">{patient.last_name.toUpperCase()} {patient.first_name}{patient.birth_date ? ` · ${fmtDate(patient.birth_date)}` : " · DOB inconnue"}</span>
                <button type="button" data-testid="clear-patient" onClick={() => { setPatient(null); setSearch(""); setResults([]); }} className="text-slate-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input ref={searchRef} data-testid="patient-search" value={search} onChange={(e) => doSearch(e.target.value)} placeholder="NOM Prénom…" className="pl-9 h-11" autoComplete="off" />
                </div>
                {results.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded-lg divide-y max-h-40 overflow-auto" data-no-enter>
                    {results.map((p) => (
                      <button type="button" key={p.id} data-testid={`patient-result-${p.id}`} onClick={() => { setPatient(p); setResults([]); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                        <span className="font-semibold">{p.last_name.toUpperCase()} {p.first_name}</span> <span className="text-slate-400">· {p.birth_date ? fmtDate(p.birth_date) : "DOB inconnue"}</span>
                      </button>
                    ))}
                  </div>
                )}
                {search.trim().length >= 2 && (
                  <div className="mt-2 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="new-patient-box">
                    <p className="text-xs font-semibold text-slate-500 mb-2">Nouveau patient (créé automatiquement à la validation)</p>
                    <div className="grid grid-cols-3 gap-2">
                      <Input data-testid="new-patient-lastname" placeholder="Nom" value={np.last_name} onChange={(e) => setNp({ ...np, last_name: e.target.value })} className="h-10" />
                      <Input data-testid="new-patient-firstname" placeholder="Prénom" value={np.first_name} onChange={(e) => setNp({ ...np, first_name: e.target.value })} className="h-10" />
                      <FrDateInput value={np.birth_date} onChange={(v) => setNp({ ...np, birth_date: v })} mode="birth" testid="new-patient-birthdate" placeholder="Naissance (facult.)" className="[&>input]:h-10 [&>input]:text-sm" />
                    </div>
                  </div>
                )}
              </>
            )}
            {similar && (
              <div data-testid="similar-patients" className="mt-2 p-3 rounded-lg bg-orange-50 border border-orange-200 space-y-2" data-no-enter>
                <p className="text-sm font-bold text-orange-800">Un patient similaire existe déjà</p>
                {similar.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 text-sm bg-white rounded-lg px-3 py-2 border border-orange-100">
                    <span><b>{p.last_name.toUpperCase()} {p.first_name}</b> · {p.birth_date ? `né(e) le ${fmtDate(p.birth_date)}` : "date de naissance inconnue"} <span className="text-slate-400 text-xs">{p.internal_id}</span></span>
                    <Button type="button" size="sm" data-testid={`use-patient-${p.id}`} onClick={() => { setPatient(p); setSimilar(null); }} className="bg-accent-600 hover:bg-accent-700 h-8">Utiliser ce patient</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" data-testid="force-new-patient" onClick={() => submit(true)} className="h-8">Créer quand même un nouveau patient</Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Type de demande</Label>
              <select data-testid="req-type" value={f.document_type} onChange={set("document_type")} className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 bg-white">
                {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              {isReq && <p className="text-xs text-orange-700 mt-1">Le chauffeur ira demander la PMT au secrétariat / service (elle n'est pas encore prête).</p>}
            </div>
            <div><Label>Établissement</Label>
              <EstablishmentPicker value={f.establishment_id} onChange={set("establishment_id")} establishments={establishments} reload={reloadEstablishments} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Service / secrétariat</Label><Input data-testid="req-service" value={f.service} onChange={set("service")} placeholder="Ex : cardiologie" className="mt-1 h-11" /></div>
            <div><Label className="text-brand-900 font-bold">Date du transport {isReq && <span className="text-red-600">*</span>}</Label><FrDateInput value={f.transport_date} onChange={set("transport_date")} testid="req-transport" className="mt-1 [&>input]:h-11" /></div>
            {isReq ? <div><Label>Heure du transport</Label><FrTimeInput value={f.transport_time} onChange={set("transport_time")} testid="req-transport-time" className="mt-1 [&>input]:h-11" /></div>
              : <div><Label>Personne à demander</Label><Input data-testid="req-contact" value={f.contact_person} onChange={set("contact_person")} className="mt-1 h-11" /></div>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isReq && <div><Label>Personne à demander <span className="text-slate-400 font-normal">(si connue)</span></Label><Input data-testid="req-contact" value={f.contact_person} onChange={set("contact_person")} placeholder="Ex : Mme Martin" className="mt-1 h-11" /></div>}
            <div><Label>Chauffeur affecté <span className="text-slate-400 font-normal">(facultatif)</span></Label>
              <select data-testid="req-driver" value={f.assigned_driver_id} onChange={set("assigned_driver_id")} className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 bg-white">
                <option value="">— Non affecté (disponible à tous) —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
              </select>
            </div>
          </div>

          <button type="button" data-testid="toggle-more" onClick={() => setMore(!more)} className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-800"><ChevronDown className={`w-4 h-4 transition-transform ${more ? "rotate-180" : ""}`} />Récupérable dès / date limite / consigne</button>
          {more && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fade-in">
              <div><Label>Récupérable dès</Label><FrDateInput value={f.available_from} onChange={set("available_from")} testid="req-available" className="mt-1 [&>input]:h-11" /></div>
              <div><Label>Date limite</Label><FrDateInput value={f.deadline} onChange={set("deadline")} testid="req-deadline" className="mt-1 [&>input]:h-11" /></div>
              <div className="sm:col-span-2"><Label>Commentaire / consigne</Label><Textarea data-testid="req-comment" value={f.comment} onChange={set("comment")} className="mt-1" rows={2} /></div>
            </div>
          )}
          {error && <p data-testid="create-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        </div>
        <DialogFooter className="items-center gap-2">
          <span className="text-xs text-slate-400 mr-auto hidden sm:inline">Entrée = créer</span>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="button" data-testid="submit-request" disabled={saving} onClick={() => submit()} className="bg-accent-600 hover:bg-accent-700 min-w-[160px]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer la demande</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
